import React, { useEffect, useState } from 'react';
import { Dropdown, Input } from 'semantic-ui-react';
import { showMessage } from '../../helpers/MessageUtility';
import { useApim } from '../../hooks/useApi';
import CalculationRunner from '../../calculation/CalculationRunner';
import { checkIfCalculationValueIsTrue, parsePageRange } from '../helpers';
import { GoFormzServices } from '../../AppSettings';
import GfModal from '../../gfcomponents/GfModal/GfModal';

export interface GeneratePdfModalProps {
  formId?: string;
  templateId?: string;
  printConfigs?: unknown[];
  onClose?: VoidFunction;
  versionId?: string;
  saveFirst?: boolean;
  saveForm?: VoidFunction;
  template?: unknown;
  form?: unknown;
}

export default function GeneratePdfModal(props: GeneratePdfModalProps) {
  const [pageRange, setPageRange] = useState('');
  const [pageOutOfRange, setPageOutOfRange] = useState(false);
  const [validPages, setValidPages] = useState(true);
  const [pageVisibility, setPageVisibility] = useState<boolean>();
  const [selectionType, setSelectionType] = useState('All');
  const [exportingHiddenPages, setExportingHiddenPages] = useState(false);
  const [selectedPages, setSelectedPages] = useState('');

  const [formAndTemplateResult, loading, loaded] = useApim<{ template: unknown; form: unknown }>(
    GoFormzServices.TemplateService,
    props.versionId ? `/formAndTemplate/${props.formId}/versions/${props.versionId}` : `/formAndTemplate/${props.formId}`,
    !!(props.form && props.template)
  );

  let template;
  let form;
  if (props.form && props.template) {
    template = props.template;
    form = props.form;
  } else if (loaded) {
    template = formAndTemplateResult.template;
    form = formAndTemplateResult.form;
  }

  useEffect(() => {
    if (template) {
      getPageVisibility(template, form).then((pv: TypeMePlease) => {
        setPageVisibility(pv);
      });
    }
  }, [form, template]);

  useEffect(() => {
    if (pageVisibility) {
      let pages;

      if (selectionType === 'All') {
        pages = template.pages.map((x) => x.number).sort((a, b) => a - b);
        setSelectedPages('');
        setValidPages(true);
        setExportingHiddenPages(!template.pdfExportUseVisible && includesHiddenPage(pages, pageVisibility));
      } else if (selectionType === 'Custom') {
        pages = parsePageRange(
          pageRange,
          template.pages.map((x) => x.number).reduce((a, b) => Math.max(a, b))
        );
        const transformedPages = transformPages(pages, pageVisibility);
        setSelectedPages(transformedPages.join(','));
        setPageOutOfRange(!!(pageRange && transformedPages.length === 0));
        setValidPages(transformedPages.length > 0);
      } else {
        // preset
        const preset = template.printConfigs.find((x) => x.name === selectionType);
        pages = parsePageRange(
          preset.pages,
          template.pages.map((x) => x.number).reduce((a, b) => Math.max(a, b))
        );
        setSelectedPages(preset.pages);
        setValidPages(true);
        setExportingHiddenPages(!template.pdfExportUseVisible && includesHiddenPage(pages, pageVisibility));
      }
    }
  }, [selectionType, pageRange, pageVisibility]);

  const handleSelectButtonClicked = async (download: boolean) => {
    // TODO enable this once we want to do async pdf downloads
    const async = false;
    const exportSubmission = {
      type: 'pdf',
      pages: selectedPages,
      forceDownload: download
    };

    if (props.saveFirst && props.saveForm) {
      await props.saveForm();
    }

    let pdfWindow = null;
    if (!download && !async) {
      pdfWindow = window.open('/loading', '_blank');
    }
    try {
      const { downloadPdf } = await import('../../helpers/PdfUtility');
      const resultJson = await downloadPdf(props.formId, props.versionId, exportSubmission, async);

      if (!async) {
        if (!download) {
          pdfWindow.location.href = resultJson.data.downloadUrl;
        } else {
          window.location.href = resultJson.data.downloadUrl;
        }
      }
    } catch (err) {
      if (pdfWindow != null) {
        pdfWindow.close();
      }
      showMessage('Error', 'There was an error generating the pdf');
    }

    props.onClose();
  };

  const handlePageRangeChanged = (e) => {
    setPageRange(e.target.value);
  };

  const selectPageRange = [
    {
      text: 'All pages',
      value: 'All'
    }
  ];

  if (pageVisibility && template && template.pages && template.pages.length > 0) {
    selectPageRange.push({
      text: 'Custom',
      value: 'Custom'
    });
  }

  if (template && template.printConfigs && template.pages && template.pages.length > 0) {
    for (const printConfig of template.printConfigs) {
      selectPageRange.push({
        text: printConfig.name,
        value: printConfig.name
      });
    }
  }

  return (
    <GfModal
      className="pdf-modal"
      title="Select Pages"
      primaryText="Download"
      secondaryText="Open"
      onPrimaryClick={() => handleSelectButtonClicked(true)}
      onSecondaryClick={() => handleSelectButtonClicked(false)}
      onClose={props.onClose}
      actionButtonProps={{ primary: { disabled: !validPages }, secondary: { disabled: !validPages } }}
    >
      <Dropdown
        placeholder="Select Pages"
        fluid
        selection
        options={selectPageRange}
        value={selectionType}
        onChange={(e, data) => setSelectionType(data.value as string)}
        loading={loading || !pageVisibility}
      />
      {selectionType === 'Custom' ? (
        <>
          <div style={{ marginTop: '8px' }}>Pages</div>
          <div style={{ marginTop: '8px' }}>
            <Input placeholder="e.g. 1-3, 4-7" value={pageRange} onChange={handlePageRangeChanged} error={pageOutOfRange} />
          </div>
        </>
      ) : null}
      {selectionType !== 'Custom' && exportingHiddenPages ? (
        <>
          <div style={{ marginTop: '16px', color: '#9b870c' }}>
            <i className="ui icon warning sign" />
            Some pages that are hidden will be included in the PDF.
          </div>
        </>
      ) : null}
    </GfModal>
  );
}

// TODO - GD-20719 web app perf cleanup

async function getPageVisibility(template, form) {
  const pageVisibility = [];
  for (const page of [...template.pages].sort((a, b) => a.number - b.number)) {
    pageVisibility.push({ pageNumber: page.number, visible: page.visible !== false });
  }

  const pageVisibleFormulas = getPageVisibleFormulas(template);

  if (pageVisibleFormulas.length > 0) {
    const calculationRunner = new CalculationRunner(form.id, template);
    await calculationRunner.init();

    for (const pageVisibleFormula of pageVisibleFormulas) {
      const result = await calculationRunner.executeFormula(pageVisibleFormula.formula, form.values);
      pageVisibility.find((x) => x.pageNumber === pageVisibleFormula.pageNumber).visible = checkIfCalculationValueIsTrue(result);
    }
  }

  return pageVisibility;
}

function getPageVisibleFormulas(template) {
  const pageVisibleFormulas = [];
  for (const page of template.pages) {
    if (page.formulas) {
      for (const formula of page.formulas) {
        if (formula.key === 'Visible') {
          pageVisibleFormulas.push({
            pageNumber: page.number,
            formula: formula.value
          });
        }
      }
    }
  }
  return pageVisibleFormulas;
}

function includesHiddenPage(pages, pageVisibility) {
  for (const page of pages) {
    const pageItem = pageVisibility.find((x) => x.pageNumber === page);
    if (!pageItem.visible) {
      return true;
    }
  }
  return false;
}

function transformPages(pages, pageVisibility) {
  const newPages = [];
  let pageOffset = 0;
  for (const { pageNumber, visible } of pageVisibility) {
    if (!visible) {
      pageOffset++;
    } else {
      const includePage = pages.indexOf(pageNumber - pageOffset) > -1;
      if (includePage) {
        newPages.push(pageNumber);
      }
    }
  }
  return newPages;
}
